import { useEffect, useState } from "react";
import { useAppSelector } from "../../../hooks/hooks";

import {
  Padding,
  Text,
  Layout,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Icon,
  IconList,
  Button,
  TextArea,
  Container,
  Margin,
  CollapsableItemList,
} from "@kidslivesafe/sc-react";

import {
  useAddReportNoteDispatch,
  useGetAllReportsWithNotesDispatch,
} from "../../../hooks/notesHook";
import {
  useNoteValidation,
  type NoteValidationResult,
} from "../../../hooks/noteValidationHook";
import { addNoteToReportParamBuilder } from "../../../utils";
import {
  CombinedReports,
  selectReportNotes,
} from "../../../features/reports/reportsSlice";

import NoteEntry from "../NoteEntry/NoteEntry";
import NoteModal, { NoteModalMode } from "../NoteModal/NoteModal";

const ReportNoteContainer: React.FC<{
  report: CombinedReports;
  printReady?: boolean;
}> = ({ report, printReady }) => {
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const getAllReportsWithNotes = useGetAllReportsWithNotesDispatch();
  const addReportNote = useAddReportNoteDispatch();
  const { getNoteValidation } = useNoteValidation();

  const [noteText, setNoteText] = useState<string>("");
  const [disableNoteSave, setDisableNoteSave] = useState<boolean>(true);

  //noteModal
  const [mode, setMode] = useState<NoteModalMode>("remove-note");
  const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
  const [savedNoteId, setSavedNoteId] = useState<number>(0);
  const [savedNoteText, setSavedNoteText] = useState<string>("");
  const [noteTextError, setNoteTextError] = useState<boolean>(false);
  const [noteErrorText, setNoteErrorText] = useState<string>("");

  const applyNoteValidation = (validation: NoteValidationResult) => {
    setDisableNoteSave(validation.disableNoteSave);
    setNoteTextError(validation.noteTextError);
    setNoteErrorText(validation.noteErrorText);
  };

  //store state
  const reportNotes = useAppSelector(selectReportNotes);
  const sortedNotes = reportNotes
    ? [...reportNotes].sort((a, b) => {
        const aDate = a.dateModified || a.dateCreated;
        const bDate = b.dateModified || b.dateCreated;

        if (aDate > bDate) {
          return -1;
        }
        if (aDate < bDate) {
          return 1;
        }
        return 0;
      })
    : [];

  const handleModalTrigger = (
    mode: NoteModalMode,
    noteId: number,
    noteText: string,
  ) => {
    setSavedNoteId(noteId);
    setSavedNoteText(noteText);
    setMode(mode);
    setShowNoteModal(true);
  };

  const handleNoteSubmit = () => {
    const addNoteParams = report
      ? addNoteToReportParamBuilder(report, noteText)
      : undefined;

    if (report && addNoteParams) {
      addReportNote(addNoteParams);
    }
    getAllReportsWithNotes();

    setNoteText("");
  };

  const handleSaveClick = () => {
    const validation = getNoteValidation(noteText, "submit");
    applyNoteValidation(validation);

    if (validation.disableNoteSave) {
      return;
    }

    handleNoteSubmit();
    setActionCompleted(true);
    setTimeout(() => {
      setActionCompleted(false);
    }, 2000);
  };

  useEffect(() => {
    applyNoteValidation(getNoteValidation(noteText, "typing"));
  }, [noteText]);
  return (
    <>
      <Container containerId="notes-section" topStroke>
        <Padding space="md">
          <Container altColor="alt">
            <Padding space="md">
              <Layout
                type="flex"
                flexProps={{
                  gap: "xxs",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text size="xxl" fontWeight="semibold" textAlign="center">
                  Add Your Note
                </Text>
                <Tooltip padding={8}>
                  <TooltipTrigger className="sc-tooltip__trigger">
                    <Text color="primary" tag="div">
                      <Icon name={IconList.IconHelp} size={16} />
                    </Text>
                  </TooltipTrigger>
                  <TooltipContent className="sc-tooltip__content">
                    Add notes related to this report here. You can review any
                    notes at a later time by visiting the "Your Report Notes"
                    section in the dashboard and navigating back to this report.
                  </TooltipContent>
                </Tooltip>
              </Layout>
              <Margin top="md" bottom="xs">
                <TextArea
                  id="noteText"
                  placeholder="Add a note..."
                  handleChange={input => {
                    setNoteText(input);
                  }}
                  value={noteText}
                  characterLimit={500}
                  error={noteTextError}
                  errorText={noteErrorText}
                />
              </Margin>
              <Button
                buttonText={
                  <Layout
                    type="flex"
                    flexProps={{ gap: "xxs", alignItems: "center" }}
                  >
                    {actionCompleted ? (
                      <Icon name={IconList.IconYes} size={16} />
                    ) : null}
                    <Text>{actionCompleted ? "Note Saved" : "Save"}</Text>
                  </Layout>
                }
                fill="fill"
                handleClick={handleSaveClick}
                color={
                  actionCompleted
                    ? "success"
                    : disableNoteSave
                      ? "disabled"
                      : "primary"
                }
              />
            </Padding>
          </Container>
          <Container altColor="grey">
            <Padding space="md">
              <Text size="xs">
                <Text size="xs" tag="b">
                  Privacy Notice:
                </Text>
                {"  "}
                Notes are private and visible only to you. Use this space to
                capture personal insights or reminders related to the report.
              </Text>
            </Padding>
          </Container>
          {reportNotes && reportNotes.length !== 0 ? (
            <Margin top="md">
              <Text fontWeight="bold">Your Notes</Text>
              <Container>
                <Padding left="sm" right="sm">
                  <CollapsableItemList numberToShow={5} isOpen={printReady}>
                    {/* TO DO sort in reverse order */}
                    {sortedNotes
                      ? sortedNotes.map(note => (
                          <NoteEntry
                            note={note}
                            key={note.id}
                            onDelete={() => {
                              handleModalTrigger(
                                "remove-note",
                                note.id,
                                note.note,
                              );
                            }}
                            onEdit={() => {
                              handleModalTrigger(
                                "edit-note",
                                note.id,
                                note.note,
                              );
                            }}
                          />
                        ))
                      : null}
                  </CollapsableItemList>
                </Padding>
              </Container>
            </Margin>
          ) : null}
        </Padding>
      </Container>
      <NoteModal
        modalMode={mode}
        setModalMode={setMode}
        displayModal={showNoteModal}
        setDisplayModal={setShowNoteModal}
        noteId={savedNoteId}
        noteText={savedNoteText}
      />
    </>
  );
};

export default ReportNoteContainer;
